import React, { createContext, useContext, useState, useEffect } from 'react';
import apiClient from '../lib/apiClient';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState(null);

  /**
   * Verify current session on mount and after auth changes
   */
  const verifySession = async () => {
    try {
      const data = await apiClient.get('/api/auth/verify');
      const authenticated = Boolean(data?.authenticated);
      setIsAuthenticated(authenticated);
      if (authenticated) {
        setUser({ username: 'aerosy' }); // Set user info
      } else {
        setUser(null);
      }
    } catch (error) {
      console.error('Session verification failed:', error);
      setIsAuthenticated(false);
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Login with TOTP code
   */
  const login = async (username, totpCode) => {
    try {
      await apiClient.post('/api/auth/login', {
        username,
        totp_code: totpCode,
      });
      await verifySession(); // Re-verify after login
      return { success: true };
    } catch (error) {
      return {
        success: false,
        message: error.message || 'Login failed',
      };
    }
  };

  /**
   * Logout and clear session
   */
  const logout = async () => {
    try {
      await apiClient.post('/api/auth/logout');
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setIsAuthenticated(false);
      setUser(null);
    }
  };

  /**
   * Check if setup is required (first time)
   */
  const checkSetupStatus = async () => {
    try {
      const data = await apiClient.get('/api/auth/setup-status');
      return data?.setup_required ?? true;
    } catch (error) {
      console.error('Setup status check failed:', error);
      return true; // Assume setup required on error
    }
  };

  // Verify session on mount
  useEffect(() => {
    verifySession();
  }, []);

  const value = {
    isAuthenticated,
    isLoading,
    user,
    login,
    logout,
    verifySession,
    checkSetupStatus,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

/**
 * Hook to use auth context
 */
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export default AuthContext;
